'use client';

import { Grid2 as Grid } from '@mui/material';
import { FC, useMemo } from 'react';
import { FieldValues, RegisterOptions } from 'react-hook-form';

import { TextFormControl, useTranslation } from '@abg/kit-base';

import { useControlsConfig } from '../../hooks/use-controls-config';
import { ContactInformationProperties } from '../../types';

export const ContactInformationNamApac: FC<ContactInformationProperties> = ({
  type = 'registration',
  i18n,
  rootPath = '',
  disabledFields,
  useZodValidation = false,
  cbpEnrollmentInfo,
}) => {
  const t = useTranslation(i18n);
  const controlsConfig = useControlsConfig(t, rootPath);
  const cbpCountryList = useMemo(() => {
    const countryList = controlsConfig

      .filter(
        (field) =>
          field.name === 'country' &&
          field.type === 'select' &&
          Array.isArray(field.selectData),
      )
      .flatMap((field) => field.selectData);

    return countryList
      .filter((item): item is { value: string; label: string } => !!item)
      .filter((item) =>
        cbpEnrollmentInfo?.supportedCountries.includes(item.value),
      );
  }, [controlsConfig, cbpEnrollmentInfo?.supportedCountries]);

  return (
    <Grid container rowSpacing={{ xs: 2.5, sm: 3 }} columnSpacing={2}>
      {controlsConfig
        .filter((control) => control.visible)
        .map((control) => (
          <Grid
            size={control.getSize(
              type === 'registration' || type === 'checkout',
            )}
            key={control.name}
          >
            <TextFormControl
              name={control.name}
              required={!!control.required}
              type={control.type}
              select={control.type === 'select'}
              selectData={
                cbpEnrollmentInfo?.isCbp &&
                control.type === 'select' &&
                control.name === 'country' &&
                cbpCountryList
                  ? cbpCountryList
                  : control.selectData
              }
              defaultValue={
                cbpEnrollmentInfo?.isCbp &&
                control.type === 'select' &&
                control.name === 'country' &&
                cbpEnrollmentInfo?.country
                  ? cbpEnrollmentInfo.country
                  : control.defaultValue
              }
              {...control.translation}
              validationRules={
                useZodValidation
                  ? undefined
                  : (control.validationRules as RegisterOptions<FieldValues>)
              }
              disabled={!!disabledFields?.[control.name]}
              textFieldProps={{
                fullWidth: true,
                slotProps: { htmlInput: { maxLength: control.maxLength } },
              }}
              testIdPrefix="registration-form"
              testId={control.testId}
            />
          </Grid>
        ))}
    </Grid>
  );
};
